import { Fragment } from 'react'

import { IconSearch } from '@tabler/icons-react'
import { Form } from 'antd'
import { lowerCase } from 'lodash'
import styled from 'styled-components'

import { Button } from '../shared-components/BUI/Button'
import { Input } from '../shared-components/BUI/Input'
import { Modal } from '../shared-components/BUI/Modal'

import { useFilters } from '@/context/filterContext'
import {
  useCheckableListView,
  useListViewInfo,
} from '@/context/listViewContext'
import { useQueryParams } from '@/context/queryParamsContext'
import { onFormChangeDebounce } from '@/utils/index'

const MultiFilterWrapper = styled.div<{ count: number }>`
  display: grid;
  // calc fixed size so text ellipsis works
  grid-template-columns: repeat(${({ count }) => count}, minmax(0, 1fr));
  gap: 1.5rem;
  align-items: flex-start;
`

const MultiFilter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const FilterTitle = styled.p`
  ${({ theme }) => theme.font.subheading01}
  color: ${({ theme }) => theme.colour.secondary.base};

  &:first-letter {
    text-transform: uppercase;
  }
`

const FilterDivider = styled.hr`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colour.neutral.border};
  margin-top: 1rem;
  margin-bottom: 1rem;

  ${({ theme: { media } }) => media.sm`
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
  `}
`

const FilterModalTitle = styled.span`
  display: block;

  &:first-letter {
    text-transform: uppercase;
  }
`

type Props = {
  open: boolean
  onClose: () => void
  showSearch?: boolean
  searchFilterKey?: string
  filters?: Array<React.ReactNode | React.ReactNode[]>
  useQuery?: boolean
}

export const CorePageListViewFilterModal: React.FC<Props> = ({
  open,
  onClose,
  filters,
  showSearch,
  searchFilterKey,
  useQuery,
}) => {
  const { clearAll } = useCheckableListView()
  const { resourceSingular } = useListViewInfo()
  const { params, updateParams, clearParams } = useQueryParams()
  const { clearFilters, routeFilters, updateFilters } = useFilters()

  const [form] = Form.useForm()

  const onClearFilters = () => {
    if (useQuery) {
      clearParams()
    } else {
      clearFilters()
    }
    clearAll()
  }

  const onSearchChange = (values: Record<string, string>) => {
    if (useQuery) {
      updateParams({ [searchFilterKey]: values.search })
    } else {
      const search = { [searchFilterKey]: [values.search as string] }
      updateFilters({ filters: { ...routeFilters.filters, ...search } })
    }
  }

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      title={<FilterModalTitle>{resourceSingular} filters</FilterModalTitle>}
      footerLayout="split"
      destroyOnClose
      footer={
        <>
          <Button variant="secondary-text" onClick={onClearFilters}>
            Clear filters
          </Button>
          <Button variant="primary" onClick={onClose}>
            Apply & close
          </Button>
        </>
      }
    >
      {showSearch && (
        <>
          <Form
            form={form}
            onValuesChange={onFormChangeDebounce(form, onSearchChange, 750)}
            initialValues={{
              search: useQuery
                ? params[searchFilterKey]
                : routeFilters.filters?.search?.[0],
            }}
          >
            <Form.Item name="search" noStyle>
              <Input
                placeholder="Search..."
                prefix={<IconSearch size={16} />}
              />
            </Form.Item>
          </Form>
          <FilterDivider />
        </>
      )}

      {filters?.map((filter, index) => {
        if (Array.isArray(filter)) {
          return (
            <Fragment key={`group-${index}`}>
              <MultiFilterWrapper count={filter.length}>
                {filter.map((filter) => (
                  <MultiFilter key={filter['key']}>
                    <FilterTitle>{filter['key']}</FilterTitle>
                    {filter}
                  </MultiFilter>
                ))}
              </MultiFilterWrapper>
              {index !== filters.length - 1 && <FilterDivider />}
            </Fragment>
          )
        }

        return (
          <Fragment key={filter['key']}>
            <FilterTitle>{lowerCase(filter['key'])}</FilterTitle>
            {filter}
            {index !== filters.length - 1 && <FilterDivider />}
          </Fragment>
        )
      })}
    </Modal>
  )
}
